import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CreateEpisodeFormValues,
  followUpStatusOptions,
  formatFollowUpStatus,
  formatFollowUpType,
  getFollowUpTaskTypeOptions,
} from "../../lib/intakeForm";
import FormControl from "../form/FormControl";
import Input from "../form/Input";

type EpisodeFollowUpsSectionProps = {
  form: UseFormReturn<CreateEpisodeFormValues>;
  stepLabel?: string;
  /** Manager/admin only: existing follow-ups can be deleted. */
  canDelete?: boolean;
  /** Called when an existing (persisted) follow-up is removed, so the page can delete it on save. */
  onRemoveExisting?: (id: string) => void;
};

function EpisodeFollowUpsSection({
  form,
  stepLabel = "Follow-ups",
  canDelete = false,
  onRemoveExisting,
}: EpisodeFollowUpsSectionProps) {
  const { t } = useTranslation();
  // Use a non-"id" key name so react-hook-form does not overwrite the server id
  // we carry on existing follow-ups.
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "followUps",
    keyName: "_rhfId",
  });

  return (
    <section className="subCard" aria-labelledby="follow-up-heading">
      <div className="cardHeaderRow">
        <h3 id="follow-up-heading" className="subCardTitle">
          {t(stepLabel)}
        </h3>

        <button
          className="secondaryButton"
          id="add-follow-up"
          onClick={() =>
            append({
              taskType: "",
              dueAt: "",
            })
          }
          type="button"
        >
          {t("Add follow-up")}
        </button>
      </div>

      {fields.length === 0 ? (
        <p className="hintText sectionHint">
          {t("No follow-up tasks yet. Add one to get started.")}
        </p>
      ) : null}

      {fields.map((field, index) => {
        const taskTypeName = `followUps.${index}.taskType` as const;
        const dueAtName = `followUps.${index}.dueAt` as const;
        const statusName = `followUps.${index}.status` as const;
        const existingId = field.id;
        const isExisting = Boolean(existingId);
        const canRemove = !isExisting || canDelete;

        return (
          <div className="followUpCard" key={field._rhfId}>
            <div className="followUpCardHeader">
              <p className="followUpTitle">
                {isExisting
                  ? t("Follow-up")
                  : t("New follow-up {{index}}", { index: index + 1 })}
              </p>
              {canRemove ? (
                <button
                  aria-label={t("Remove follow-up {{index}}", {
                    index: index + 1,
                  })}
                  className="secondaryButton"
                  onClick={() => {
                    if (isExisting && existingId) {
                      onRemoveExisting?.(existingId);
                    }
                    remove(index);
                  }}
                  type="button"
                >
                  {t("Remove")}
                </button>
              ) : null}
            </div>

            <div className="formColumns">
              <FormControl<CreateEpisodeFormValues>
                label={t("Task type")}
                name={taskTypeName}
              >
                <select
                  className="inputControl"
                  {...form.register(taskTypeName, {
                    validate: (value) =>
                      (typeof value === "string" && value.trim().length > 0) ||
                      t("Task type is required"),
                  })}
                >
                  <option value="">{t("Select task type")}</option>
                  {getFollowUpTaskTypeOptions(field.taskType).map(
                    (taskType) => (
                      <option key={taskType} value={taskType}>
                        {t(formatFollowUpType(taskType))}
                      </option>
                    ),
                  )}
                </select>
              </FormControl>

              <FormControl<CreateEpisodeFormValues>
                label={t("Due at")}
                name={dueAtName}
              >
                <Input<CreateEpisodeFormValues>
                  name={dueAtName}
                  rules={{
                    validate: (value) =>
                      (typeof value === "string" && value.trim().length > 0) ||
                      t("Due date and time is required"),
                  }}
                  type="datetime-local"
                />
              </FormControl>

              {isExisting ? (
                <FormControl<CreateEpisodeFormValues>
                  label={t("Status")}
                  name={statusName}
                >
                  <select
                    className="inputControl"
                    {...form.register(statusName)}
                  >
                    {followUpStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {t(formatFollowUpStatus(status))}
                      </option>
                    ))}
                  </select>
                </FormControl>
              ) : null}
            </div>

            {field.description ? (
              <dl className="followUpDescription">
                <dt className="summaryLabel">{t("Description")}</dt>
                <dd className="summaryValue">{field.description}</dd>
              </dl>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

export default EpisodeFollowUpsSection;
